export enum TranscriptionStatus {
  UPLOADING = 'UPLOADING',
  PROCESSING = 'PROCESSING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  DRAFT = 'DRAFT'
}

// Mapped from Swagger FileStatus: uploading, processing, ready, failed, deleted, transcription_only
export type ApiFileStatus =
  | 'uploading'
  | 'processing'
  | 'claim_pending'
  | 'ready'
  | 'failed'
  | 'deleted'
  | 'transcription_only';

export interface Transcription {
  id: string;
  filename: string;
  duration: string;
  date: string;
  status: TranscriptionStatus;
  progress: number; // 0-100
  language?: string;
  summary?: string;
  content?: TranscriptSegment[];
}

export interface TranscriptSegment {
  id: string;
  startTime: number;
  endTime: number;
  speaker: string;
  text: string;
}

export interface UserStats {
  usedMinutes: number;
  totalMinutes: number;
  filesCount: number;
}

export enum UploadStep {
  SELECT_FILE = 0,
  CONFIRM_INFO = 1,
  OPTIONS = 2,
  UPLOADING = 3,
  COMPLETE = 4
}

// Swagger Types
export interface FileResponse {
  id: string;
  name: string;
  original_name: string;
  duration: number; // seconds
  file_size: number; // bytes
  file_type: string;
  mime_type: string;
  created_at: string;
  status: ApiFileStatus;
  source_type: 'upload' | 'youtube' | 'url';
  language: string;
}

export interface ListFilesResponse {
  files: FileResponse[];
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
}

// Auth Types from Swagger
export interface LoginRequest {
  email: string;
  password: string;
  device_info?: string;
  turnstile_token?: string;
}

export interface RegisterRequest {
  email: string;
  name: string;
  password: string;
  language?: string;
  timezone?: string;
  referral_code?: string;
  fingerprint?: string; // Device fingerprint for linking anonymous data
  turnstile_token?: string;
}

// User role type
export type UserRole = 'user' | 'admin' | 'super_admin';

export interface User {
  id: string;
  name: string;
  email: string;
  avatar_url?: string;
  language: string;
  timezone: string;
  role: UserRole;
  is_active: boolean;
  email_verified: boolean;
  two_factor_enabled: boolean;
  last_login_at?: string;
  created_at: string;
  updated_at: string;
}

export interface AuthResponse {
  access_token: string;
  expires_at: string;
  refresh_token: string;
  user: User;
}

export type OAuthProvider = 'google' | 'github' | 'apple' | 'wechat';

export interface OAuthAccount {
  provider: OAuthProvider;
  email?: string;
  name?: string;
  avatar_url?: string;
  created_at: string;
}

// Billing Types from Swagger
export type PlanType = 'free' | 'go' | 'plus' | 'pro';
export type BillingInterval = 'monthly' | 'yearly';
export type SubscriptionStatus = 'active' | 'cancelled' | 'past_due' | 'expired' | 'trialing';
export type PaymentStatus = 'pending' | 'succeeded' | 'failed' | 'refunded';

export interface Plan {
  id: string;
  name: string;
  description?: string;
  plan_type: PlanType;
  monthly_minutes: number;
  price_monthly: number; // In cents
  price_yearly: number; // In cents
  features: string[]; // JSON array of features
  max_file_size: number; // In bytes
  max_file_duration: number; // In seconds
  daily_file_limit: number; // -1 for unlimited
  retention_days: number;
  api_rate_limit: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface Subscription {
  id: string;
  user_id: string;
  plan_id: string;
  status: SubscriptionStatus;
  billing_interval: BillingInterval;
  current_period_start: string;
  current_period_end: string;
  cancel_at_period_end: boolean;
  cancelled_at?: string;
  trial_end?: string;
  pending_plan_id?: string;
  pending_interval?: BillingInterval;
  pending_change_type?: 'upgrade' | 'downgrade';
  pending_effective_at?: string;
  created_at: string;
  updated_at: string;
  plan?: Plan;
}

export interface Quota {
  monthly_minutes: number;
  bonus_minutes: number;
  used_minutes: number;
  remaining_minutes: number;
  daily_files_used: number;
  daily_files_limit: number;
  can_upload_today: boolean;
  daily_reset_at?: string;
  period_start?: string;
  period_end?: string;
}

export interface UsageSummary {
  total_minutes: number;
  transcription_count: number;
  translation_count?: number;
  summary_count?: number;
  mindmap_count?: number;
  keypoints_count?: number;
  export_count?: number;
  by_type?: Record<string, { minutes: number; count: number }>;
}

export interface UsageResponse {
  period: {
    start: string;
    end: string;
  };
  usage: UsageSummary;
  quota?: Quota;
}

export interface Invoice {
  id: string;
  user_id: string;
  subscription_id?: string;
  amount: number; // In cents
  currency: string;
  status: PaymentStatus;
  description?: string;
  invoice_url?: string;
  receipt_url?: string;
  failure_message?: string;
  paid_at?: string;
  refunded_at?: string;
  created_at: string;
  updated_at: string;
}

export interface ApiKey {
  id: string;
  name?: string;
  key_prefix: string;
  last_used_at?: string;
  last_used_ip?: string;
  revoked_at?: string;
  created_at: string;
  updated_at: string;
}

export interface ListApiKeysResponse {
  keys: ApiKey[];
}

export interface CreateApiKeyResponse {
  api_key: string;
  key: ApiKey;
}

export interface InvoicesResponse {
  invoices: Invoice[];
  total: number;
  limit: number;
  offset: number;
}

export interface PlansResponse {
  plans: Plan[];
}

export interface SubscriptionResponse {
  subscription: Subscription;
}

export interface QuotaResponse {
  quota: Quota;
}

export interface SubscribeRequest {
  plan_id: string;
  interval: BillingInterval;
}

export interface CancelSubscriptionRequest {
  immediately: boolean;
}

export interface DailyActivity {
  date: string;
  minutes: number;
}

export interface UsageActivityResponse {
  activity: DailyActivity[];
}

export interface PlanChangePreview {
  change_type: 'upgrade' | 'downgrade';
  current_plan_id: string;
  new_plan_id: string;
  current_interval: BillingInterval;
  new_interval: BillingInterval;
  amount_due: number; // In cents
  credit: number; // In cents
  charge: number; // In cents
  currency: string;
  effective_at: string;
  immediate: boolean;
  description: string;
}

export interface PlanChangePreviewResponse {
  preview: PlanChangePreview;
}

export interface PlanChangeRequest {
  plan_id: string;
  interval: BillingInterval;
}

// Stripe Checkout types
export interface CreateCheckoutSessionRequest {
  plan_id: string;
  interval: BillingInterval;
  success_url?: string;
  cancel_url?: string;
  promotion_code?: string;
}

export interface CheckoutSessionResponse {
  checkout_url: string;
  session_id: string;
}

export interface CustomerPortalResponse {
  portal_url: string;
}
